import { Field, Input } from "@/components/base/FormControls";
import LogoUpload from "@/components/base/LogoUpload";

interface DocumentIdentityCardProps {
  /** Aperçu du logo (data URL). */
  logo: string;
  onLogoChange: (value: string) => void;
  /** Coordonnées d'identité (reprises du profil, affichées en rappel). */
  nom: string;
  adresse: string;
  telephone: string;
  email: string;
  /** Champs propres aux documents. */
  siteWeb: string;
  onSiteWebChange: (value: string) => void;
  ifu: string;
  onIfuChange: (value: string) => void;
  rccm: string;
  onRccmChange: (value: string) => void;
  logoLabel?: string;
  disabled?: boolean;
}

/**
 * Section « Identité de mes documents » : logo + coordonnées affichés
 * automatiquement dans l'en-tête et le pied de page des PDF générés par le
 * compte connecté. Le nom, l'adresse et le téléphone proviennent de la section
 * Profil (source unique) et sont affichés ici en rappel.
 */
export default function DocumentIdentityCard({
  logo,
  onLogoChange,
  nom,
  adresse,
  telephone,
  email,
  siteWeb,
  onSiteWebChange,
  ifu,
  onIfuChange,
  rccm,
  onRccmChange,
  logoLabel = "Logo",
  disabled = false,
}: DocumentIdentityCardProps) {
  return (
    <section className="flex flex-col gap-5 rounded-lg border border-background-200 bg-background-100/60 p-4 md:p-5">
      <header className="flex items-center gap-2.5">
        <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-accent-100 text-accent-800">
          <i className="ri-file-list-3-line text-base" aria-hidden="true" />
        </span>
        <div>
          <h4 className="font-heading text-sm font-semibold text-foreground-950 md:text-base">
            Identité de mes documents
          </h4>
          <p className="text-xs text-foreground-500">
            Logo et coordonnées affichés dans l'en-tête et le pied de page de vos documents PDF.
          </p>
        </div>
      </header>

      <LogoUpload
        value={logo}
        onChange={onLogoChange}
        label={logoLabel}
        hint="PNG ou JPG, 1 Mo maximum. Conservé dans votre compte et affiché dans l'en-tête de vos PDF."
      />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Nom de l'entreprise / agence / propriétaire" htmlFor="identite-nom" className="sm:col-span-2">
          <Input id="identite-nom" value={nom} icon="ri-building-2-line" readOnly disabled />
        </Field>
        <Field label="Adresse" htmlFor="identite-adresse" className="sm:col-span-2" hint="Reprise du champ Profil.">
          <Input id="identite-adresse" value={adresse} icon="ri-map-pin-line" readOnly disabled />
        </Field>
        <Field label="Téléphone" htmlFor="identite-telephone" hint="Repris du champ Profil.">
          <Input id="identite-telephone" value={telephone} icon="ri-phone-line" readOnly disabled />
        </Field>
        <Field label="Email" htmlFor="identite-email" hint="Adresse de connexion du compte.">
          <Input id="identite-email" value={email} icon="ri-mail-line" readOnly disabled />
        </Field>
        <Field label="Site web" htmlFor="identite-site">
          <Input
            id="identite-site"
            value={siteWeb}
            onChange={(event) => onSiteWebChange(event.target.value)}
            placeholder="https://www.exemple.bf"
            icon="ri-global-line"
            maxLength={160}
            disabled={disabled}
          />
        </Field>
        <Field label="IFU (facultatif)" htmlFor="identite-ifu">
          <Input
            id="identite-ifu"
            value={ifu}
            onChange={(event) => onIfuChange(event.target.value)}
            placeholder="Identifiant Financier Unique"
            maxLength={40}
            disabled={disabled}
          />
        </Field>
        <Field label="RCCM (facultatif)" htmlFor="identite-rccm" className="sm:col-span-2">
          <Input
            id="identite-rccm"
            value={rccm}
            onChange={(event) => onRccmChange(event.target.value)}
            placeholder="Registre du Commerce et du Crédit Mobilier"
            maxLength={60}
            disabled={disabled}
          />
        </Field>
      </div>

      <p className="flex items-start gap-1.5 text-xs text-foreground-400">
        <i className="ri-information-line mt-0.5 text-sm" aria-hidden="true" />
        Le nom, l'adresse et le téléphone se modifient dans la section Profil ci-dessus.
      </p>
    </section>
  );
}